import {
   addDays,
   addWeeks,
   endOfWeek,
   format,
   isSameDay,
   isToday,
   startOfWeek,
} from "date-fns";
import { ro } from "date-fns/locale";

const WEEK_OPTS = { weekStartsOn: 1, locale: ro };

/** Ore lecții — aliniat cu Calendar MAX / SAddProg (fără sloturi în pauza de prânz) */
export const STUDENT_LESSON_TIME_SLOTS = [
   "07:00",
   "08:30",
   "10:00",
   "11:30",
   "13:30",
   "15:00",
   "16:30",
   "18:00",
   "19:30",
];

export function parseSlotTimeLabel(label) {
   const [hour, minute] = String(label || "00:00").split(":").map(Number);
   return { hour: hour || 0, minute: minute || 0 };
}

export function formatEventSlotLabel(date) {
   if (!date) return "";
   const hour = String(date.getHours()).padStart(2, "0");
   const minute = String(date.getMinutes()).padStart(2, "0");
   return `${hour}:${minute}`;
}

export function snapDateToSlotLabel(date) {
   const exact = formatEventSlotLabel(date);
   if (STUDENT_LESSON_TIME_SLOTS.includes(exact)) return exact;

   const eventMinutes = date.getHours() * 60 + date.getMinutes();
   let best = STUDENT_LESSON_TIME_SLOTS[0];
   let bestDiff = Infinity;

   for (const slot of STUDENT_LESSON_TIME_SLOTS) {
      const { hour, minute } = parseSlotTimeLabel(slot);
      const slotMinutes = hour * 60 + minute;
      const diff = Math.abs(eventMinutes - slotMinutes);
      if (diff < bestDiff) {
         bestDiff = diff;
         best = slot;
      }
   }

   return best;
}

export function buildDaySlotEventMap(events, day) {
   const map = new Map();

   for (const event of events) {
      if (!event?.start || !isSameDay(event.start, day)) continue;

      const slot = snapDateToSlotLabel(event.start);
      const existing = map.get(slot);

      if (!existing) {
         map.set(slot, event);
         continue;
      }

      const existingExact = formatEventSlotLabel(existing.start) === slot;
      const currentExact = formatEventSlotLabel(event.start) === slot;
      if (currentExact && !existingExact) {
         map.set(slot, event);
      }
   }

   return map;
}

/** Index unic pentru toate zilele cu evenimente — evită recalcul per coloană. */
export function indexEventsByDaySlot(events = []) {
   const byDay = new Map();

   for (const event of events) {
      if (!event?.start) continue;

      const dayKey = event.start.toDateString();
      const slot = snapDateToSlotLabel(event.start);

      if (!byDay.has(dayKey)) byDay.set(dayKey, new Map());
      const slotMap = byDay.get(dayKey);
      const existing = slotMap.get(slot);

      if (!existing) {
         slotMap.set(slot, event);
         continue;
      }

      // o lecție anulată și una vie în aceeași celulă: se vede cea vie
      const existingInactive = Boolean(existing.cardItem?.inactive);
      const currentInactive = Boolean(event.cardItem?.inactive);
      if (existingInactive !== currentInactive) {
         if (existingInactive) slotMap.set(slot, event);
         continue;
      }

      const existingExact = formatEventSlotLabel(existing.start) === slot;
      const currentExact = formatEventSlotLabel(event.start) === slot;
      if (currentExact && !existingExact) {
         slotMap.set(slot, event);
      }
   }

   return byDay;
}

export function findEventForSlot(events, day, slotLabel) {
   return buildDaySlotEventMap(events, day).get(slotLabel) ?? null;
}

export function generateCalendarDays(anchorDate = new Date(), before = 60, after = 60) {
   const start = addDays(anchorDate, -before);
   const total = before + after + 1;
   return Array.from({ length: total }, (_, index) => addDays(start, index));
}

export function formatVisibleDaysLabel(days = []) {
   if (!days.length) return "";
   if (days.length === 1) {
      const { day, weekday } = formatDayHeader(days[0]);
      return `${day} ${weekday}`;
   }

   const first = days[0];
   const last = days[days.length - 1];
   const shortMonth = (date) =>
      format(date, "MMM", { locale: ro }).replaceAll(".", "");

   if (first.getMonth() === last.getMonth()) {
      return `${format(first, "d", { locale: ro })}–${format(last, "d", {
         locale: ro,
      })} ${shortMonth(first)}`;
   }

   return `${format(first, "d", { locale: ro })} ${shortMonth(first)} – ${format(
      last,
      "d",
      { locale: ro },
   )} ${shortMonth(last)}`;
}

export function getCenteredStartIndex(days, visibleCount, targetDate = new Date()) {
   const targetIdx = days.findIndex((day) => isSameDay(day, targetDate));
   if (targetIdx < 0) return 0;

   const centered = targetIdx - Math.floor(visibleCount / 2);
   const maxStart = Math.max(0, days.length - visibleCount);
   return Math.max(0, Math.min(centered, maxStart));
}

export function toFloatingDate(val) {
   if (!val) return null;

   if (val instanceof Date && !Number.isNaN(val.getTime())) {
      return new Date(
         val.getFullYear(),
         val.getMonth(),
         val.getDate(),
         val.getHours(),
         val.getMinutes(),
         val.getSeconds(),
         val.getMilliseconds(),
      );
   }

   if (typeof val === "string") {
      const matchDateTime = val.match(
         /^(\d{4})-(\d{2})-(\d{2})[T\s](\d{2}):(\d{2})(?::(\d{2}))?(?:\.\d+)?(?:Z|[+-]\d{2}:\d{2})?$/,
      );
      if (matchDateTime) {
         const [, year, month, day, hour, minute, seconds] = matchDateTime;
         return new Date(
            Number(year),
            Number(month) - 1,
            Number(day),
            Number(hour),
            Number(minute),
            seconds ? Number(seconds) : 0,
            0,
         );
      }

      const matchDateOnly = val.match(/^(\d{4})-(\d{2})-(\d{2})$/);
      if (matchDateOnly) {
         const [, year, month, day] = matchDateOnly;
         return new Date(Number(year), Number(month) - 1, Number(day), 0, 0, 0, 0);
      }
   }

   const parsed = new Date(val);
   if (!Number.isNaN(parsed.getTime())) {
      return new Date(
         parsed.getFullYear(),
         parsed.getMonth(),
         parsed.getDate(),
         parsed.getHours(),
         parsed.getMinutes(),
         parsed.getSeconds(),
         parsed.getMilliseconds(),
      );
   }

   return null;
}

export function formatFloatingDateTime(value, locale = "ro-RO") {
   const date = toFloatingDate(value);
   if (!date) return "—";

   return date.toLocaleString(locale, {
      weekday: "short",
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
   });
}

export function mapReservationsToEvents(items = []) {
   return items
      .map((item) => {
         const start = toFloatingDate(item.startTime);
         if (!start) return null;

         const end = item.endTime
            ? toFloatingDate(item.endTime)
            : new Date(start.getTime() + 90 * 60 * 1000);

         return {
            id: item.id,
            title: "Programare",
            start,
            end,
            instructor: item.instructor,
            phone: item.phone || item.instructor?.phone,
            isConfirmed: item.isConfirmed,
            gearbox: item.gearbox,
            sector: item.sector,
            raw: item,
         };
      })
      .filter(Boolean);
}

export function getWeekStart(date) {
   return startOfWeek(date, WEEK_OPTS);
}

export function getWeekDays(weekStart) {
   return Array.from({ length: 7 }, (_, index) => addDays(weekStart, index));
}

export function formatWeekRangeLabel(weekStart) {
   const weekEnd = endOfWeek(weekStart, WEEK_OPTS);
   const sameMonth = weekStart.getMonth() === weekEnd.getMonth();

   const shortMonth = (date) =>
      format(date, "MMM", { locale: ro }).replaceAll(".", "");

   if (sameMonth) {
      return `${format(weekStart, "d", { locale: ro })}–${format(weekEnd, "d", {
         locale: ro,
      })} ${shortMonth(weekStart)}`;
   }

   return `${format(weekStart, "d", { locale: ro })} ${shortMonth(
      weekStart,
   )} – ${format(weekEnd, "d", { locale: ro })} ${shortMonth(weekEnd)}`;
}

export function formatDayHeader(date) {
   const day = format(date, "d", { locale: ro });
   const weekday = format(date, "EEE", { locale: ro }).replaceAll(".", "");
   return { day, weekday };
}

export function formatTimeLabel(date) {
   return format(date, "HH:mm", { locale: ro });
}

export function isSameLocalDay(left, right) {
   return isSameDay(left, right);
}

export { isToday };

export function shiftWeek(date, deltaWeeks) {
   return addWeeks(date, deltaWeeks);
}
